import qs from 'qs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries, useQuery } from 'react-query'

import { useBankAccountByBookkeepingAccountId } from '@features/bankAccounts/hooks/useBankAccountByBookkeepingAccountId'
import { fetchContacts } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultCacheTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { useOrganizationUiSetup } from '../../../hooks/useOrganizationUiSetup'
import {
  fetchBillsForMatching,
  fetchExternalInvoicesForMatching,
  fetchInvoicesForMatching,
  fetchPostingsForMatching,
} from '../query-api'
import { BookkeepingItem } from '../types/bookkeepingItem'
import { mapBill, mapExternalInvoice, mapInvoice, mapPosting } from '../utils'

export interface UseBookkeepingItemsParams {
  accountId?: string
  enabled?: boolean
}

export interface UseBookkeepingItemsResult {
  items: BookkeepingItem[]
  isLoading: boolean
  isError: boolean
}

export const useBookkeepingItems = ({
  accountId,
  enabled: enabledProp = true,
}: UseBookkeepingItemsParams): UseBookkeepingItemsResult => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isUiSetupAccounting } = useOrganizationUiSetup()
  const organizationId = organization?.id
  const { reconciliationStartDate } = useBankAccountByBookkeepingAccountId(accountId, { enabled: enabledProp })

  const { data: contactsData } = useQuery(
    [QueryKeys.Contacts, organizationId],
    () => fetchContacts({ organizationId: organizationId as string }),
    {
      enabled: enabledProp && !!organizationId,
      staleTime: defaultCacheTime,
    },
  )

  const contactsMap = useMemo(() => {
    const contacts = contactsData?.contacts || []

    return new Map(contacts.flatMap((contact) => (contact.id && contact.name ? [[contact.id, contact.name]] : [])))
  }, [contactsData])

  const minEntryDateParam = useMemo(
    () => (reconciliationStartDate ? { minEntryDate: reconciliationStartDate } : {}),
    [reconciliationStartDate],
  )

  const approvedUnpaidQueryString = useMemo(
    () => qs.stringify({ organizationId, state: 'approved', isPaid: false, ...minEntryDateParam }),
    [organizationId, minEntryDateParam],
  )

  const externalInvoicesQueryString = useMemo(
    () => qs.stringify({ organizationId, isPaid: false, isVoided: false, ...minEntryDateParam }),
    [organizationId, minEntryDateParam],
  )

  const postingsQueryString = useMemo(
    () =>
      qs.stringify({
        accountId,
        isBankMatched: false,
        isVoided: false,
        isNotZero: true,
        include: 'posting.transaction',
        ...minEntryDateParam,
      }),
    [accountId, minEntryDateParam],
  )

  const queries = useQueries([
    {
      queryKey: [QueryKeys.Invoices, approvedUnpaidQueryString],
      queryFn: () => fetchInvoicesForMatching(approvedUnpaidQueryString),
      enabled: enabledProp && !!organizationId && isUiSetupAccounting,
      staleTime: defaultCacheTime,
      keepPreviousData: true,
    },
    {
      queryKey: [QueryKeys.Bills, approvedUnpaidQueryString],
      queryFn: () => fetchBillsForMatching(approvedUnpaidQueryString),
      enabled: enabledProp && !!organizationId,
      staleTime: defaultCacheTime,
      keepPreviousData: true,
    },
    {
      queryKey: [QueryKeys.ExternalInvoices, externalInvoicesQueryString],
      queryFn: () => fetchExternalInvoicesForMatching(externalInvoicesQueryString),
      enabled: enabledProp && !!organizationId && !isUiSetupAccounting,
      staleTime: defaultCacheTime,
      keepPreviousData: true,
    },
    {
      queryKey: [QueryKeys.Postings, postingsQueryString],
      queryFn: () => fetchPostingsForMatching(postingsQueryString),
      enabled: enabledProp && !!accountId,
      staleTime: defaultCacheTime,
      keepPreviousData: true,
    },
  ])

  const [invoicesQuery, billsQuery, externalInvoicesQuery, postingsQuery] = queries

  const transactionsMap = useMemo(
    () => new Map((postingsQuery.data?.transactions ?? []).map((transaction) => [transaction.id, transaction])),
    [postingsQuery.data?.transactions],
  )

  const items = useMemo<BookkeepingItem[]>(() => {
    return [
      ...(invoicesQuery.data?.invoices?.map((invoice) => mapInvoice(invoice, contactsMap, t)) || []),
      ...(billsQuery.data?.bills?.map((bill) => mapBill(bill, contactsMap, t)) || []),
      ...(externalInvoicesQuery.data?.externalInvoices?.map((externalInvoice) =>
        mapExternalInvoice(externalInvoice, contactsMap, t),
      ) || []),
      ...(postingsQuery.data?.postings?.map((posting) =>
        mapPosting(posting, t, transactionsMap.get(posting.transactionId)),
      ) || []),
    ]
  }, [
    invoicesQuery.data?.invoices,
    billsQuery.data?.bills,
    externalInvoicesQuery.data?.externalInvoices,
    postingsQuery.data?.postings,
    contactsMap,
    t,
    transactionsMap,
  ])

  const isLoading = queries.some((query) => query.isLoading)
  const isError = queries.some((query) => query.isError)

  return {
    items,
    isLoading,
    isError,
  }
}
